import api from "@/lib/api";
import { getPushContext, getDeviceType, isStandalonePWA } from "@/lib/device";

function urlB64ToUint8Array(base64) {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(b64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export function isIOS() {
  const ua = navigator.userAgent || "";
  const iOSDevice = /iPad|iPhone|iPod/.test(ua);
  const iPadOS = navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1;
  return iOSDevice || iPadOS;
}

// True when running as an installed PWA (home screen / standalone), not a browser tab.
export function isStandalone() {
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.matchMedia?.("(display-mode: minimal-ui)").matches ||
    window.navigator.standalone === true
  );
}

export function pushSupported() {
  return "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
}

async function subscribeVapid() {
  const reg = await navigator.serviceWorker.ready;
  const { data } = await api.get("/push/vapid-public-key");
  if (!data?.publicKey) throw new Error("no-vapid-key");
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlB64ToUint8Array(data.publicKey),
    });
  }
  await api.post("/push/web-subscribe", { subscription: sub.toJSON() });
  return sub;
}

// Standard Web Push API (VAPID) — works on iOS Safari 16.4+ installed PWA, and Android/desktop
// browsers, on ANY HTTPS origin (no third-party dashboard). The subscription is tied to the
// logged-in user server-side via the auth token on /push/web-subscribe. Returns {ok, reason}.
// MUST be called from an explicit user action (permission prompt). iOS requires the PWA installed.
export async function enableWebPush() {
  const ctx = getPushContext();
  if (ctx === "native-app") return { ok: false, reason: "native" };
  if (ctx === "ios-pwa-not-installed") return { ok: false, reason: "ios-not-installed" };
  if (!pushSupported()) return { ok: false, reason: "unsupported" };
  try {
    const perm = await Notification.requestPermission();
    if (perm !== "granted") return { ok: false, reason: perm === "denied" ? "denied" : "dismissed" };
    await subscribeVapid();
    return { ok: true };
  } catch (e) {
    if (typeof Notification !== "undefined" && Notification.permission === "denied") return { ok: false, reason: "denied" };
    return { ok: false, reason: "error" };
  }
}

// Current OS-level notification permission for this installed app / browser tab.
// Returns 'granted' | 'denied' | 'default' | 'unsupported'.
export function permissionState() {
  if (typeof Notification === "undefined") return "unsupported";
  return Notification.permission;
}

// Sanity check (Apple checklist item 6): show a notification straight from the service
// worker with NO server round-trip. Confirms the notification UI + SW work on the device
// before debugging the full push pipeline. Requires permission already granted.
export async function showLocalTest() {
  if (!("serviceWorker" in navigator)) return { ok: false, reason: "unsupported" };
  if (typeof Notification === "undefined" || Notification.permission !== "granted")
    return { ok: false, reason: "not-granted" };
  try {
    const reg = await navigator.serviceWorker.ready;
    await reg.showNotification("RoyalChat Nova", {
      body: "✅ Test notification — notifications are working on this device!",
      icon: "/icons/icon-192.png",
      badge: "/icons/icon-192.png",
      tag: "rc-local-test",
      data: { url: "/app", type: "test" },
    });
    return { ok: true };
  } catch (e) {
    return { ok: false, reason: "error" };
  }
}

// --- Enable-notifications popup eligibility --------------------
const PUSH_PROMPT_KEY = "rc_push_prompt_dismissed_at";
const PUSH_BLOCKED_KEY = "rc_push_blocked_hint_at";
export const PUSH_PROMPT_COOLDOWN_DAYS = 5; // re-ask at most every N days after "Not now"

export function dismissPushPrompt() {
  try { localStorage.setItem(PUSH_PROMPT_KEY, String(Date.now())); } catch (e) {}
}

export function dismissBlockedHint() {
  try { localStorage.setItem(PUSH_BLOCKED_KEY, String(Date.now())); } catch (e) {}
}

// Decides what (if anything) to show on app open:
//   "enable"      → the Enable-notifications popup (permission is "default")
//   "blocked-ios" → an "enable in iOS Settings → RoyalChat Nova" hint (iOS, permission denied)
//   null          → show nothing
// iOS re-prompts to enable on EVERY open; the blocked hint honours a cooldown so it
// isn't shown on literally every open (it's not actionable in-app).
export async function pushPromptMode(cooldownDays = PUSH_PROMPT_COOLDOWN_DAYS) {
  if (getPushContext() === "native-app") return null;
  if (!pushSupported()) return null;
  // Only an installed PWA (standalone, any OS) or a desktop browser — never a mobile browser tab.
  if (!isStandalonePWA() && getDeviceType() !== "desktop") return null;
  const perm = Notification.permission;
  const ios = getDeviceType() === "ios";
  if (perm === "granted") return null;
  if (perm === "denied") {
    if (!ios) return null; // only iOS gets the Settings hint
    try {
      const at = parseInt(localStorage.getItem(PUSH_BLOCKED_KEY) || "0", 10);
      if (at && (Date.now() - at) / 86400000 < cooldownDays) return null;
    } catch (e) {}
    return "blocked-ios";
  }
  // perm === "default": iOS re-prompts every open; others honour the cooldown.
  if (!ios) {
    try {
      const at = parseInt(localStorage.getItem(PUSH_PROMPT_KEY) || "0", 10);
      if (at && (Date.now() - at) / 86400000 < cooldownDays) return null;
    } catch (e) {}
  }
  return "enable";
}

// Decides whether to show the one-time enable-notifications popup on app open.
// Rules: skip if native shell or iOS-not-installed; skip if unsupported; skip if denied;
// skip if already granted (subscribed, or will silently re-subscribe); otherwise (default)
// show, respecting the cooldown since the last "Not now".
export async function shouldShowPushPrompt(cooldownDays = PUSH_PROMPT_COOLDOWN_DAYS) {
  return (await pushPromptMode(cooldownDays)) === "enable";
}

// Silent refresh on app load — re-subscribes if the user already granted permission. Handles the
// iOS quirk where a subscription can go stale after ~1–2 weeks of inactivity. No prompt is shown.
export async function refreshWebPush() {
  if (getPushContext() === "native-app") return;
  if (!pushSupported() || Notification.permission !== "granted") return;
  try { await subscribeVapid(); } catch (e) { /* ignore */ }
}

// Called on logout: detach this device's push subscription from the account so the next
// user on a shared/installed PWA does NOT keep receiving the previous account's notifications.
export async function disableWebPush() {
  if (!("serviceWorker" in navigator)) return;
  try {
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    if (sub) {
      try { await api.post("/push/web-unsubscribe", { endpoint: sub.endpoint }); } catch (e) { /* ignore */ }
      try { await sub.unsubscribe(); } catch (e) { /* ignore */ }
    }
  } catch (e) { /* ignore */ }
}
